import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Heading, Text, Spinner, Button } from "grommet";
import { Wifi, FormPrevious, Refresh } from "grommet-icons";
import GatewayService from "@services/gateway.service";
import { presentToastErrorContent } from "@shared/Toast";
import { useTranslation } from "react-i18next";

export default function GatewayDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const gateway = location.state?.gateway;
  const [connectedDoors, setConnectedDoors] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadDoors = async () => {
    if (!gateway) return;
    setLoading(true);
    try {
      const res = await GatewayService.getGateway(gateway.gatewayId);
      setConnectedDoors(res.data || []);
    } catch (error) {
      presentToastErrorContent(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDoors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!gateway) {
    return (
      <Box pad="medium">
        <Text>{t("gateways.notFound")}</Text>
      </Box>
    );
  }

  const getSignalInfo = (rssi = 0) => {
    if (rssi > -75)
      return { label: t("gateways.signalStrong"), color: "status-ok" };
    if (rssi > -85)
      return { label: t("gateways.signalMedium"), color: "status-warning" };
    return { label: t("gateways.signalWeak"), color: "status-critical" };
  };

  return (
    <Box
      align="center"
      justify="start"
      pad="medium"
      gap="medium"
      flex="grow"
      margin={{ bottom: "300px" }}
      width="100%"
    >
      {/* Header */}
      <Box
        direction="row"
        justify="between"
        align="center"
        width="100%"
        gap="medium"
      >
        <Box direction="row" align="center" gap="small">
          <Button
            icon={<FormPrevious />}
            onClick={() => navigate("/gateways")}
            plain
          />
          <Box>
            <Heading level="2" margin="none">
              {gateway.gatewayName}
            </Heading>
            <Text size="small" color="dark-6">
              MAC: {gateway.gatewayMac}
            </Text>
          </Box>
        </Box>
        <Button
          icon={loading ? <Spinner size="xsmall" /> : <Refresh />}
          label={t("gateways.refresh")}
          onClick={loadDoors}
          disabled={loading}
        />
      </Box>

      {/* Connected Doors */}
      <Box width="100%" style={{ maxWidth: 700 }}>
        <Heading level="3" margin={{ bottom: "small" }}>
          {t("gateways.connectedDoors")}
        </Heading>

        {!loading && connectedDoors.length === 0 && (
          <Box pad="large" background="light-2" round="medium">
            <Text weight="bold">{t("gateways.noDoors")}</Text>
            <Text size="small" color="dark-6">
              {t("gateways.noDoorsDesc")}
            </Text>
          </Box>
        )}

        <Box gap="small">
          {connectedDoors.map((door) => {
            const { label: signalLabel, color: signalColor } = getSignalInfo(
              door.rssi,
            );

            return (
              <Box
                key={door._id}
                pad="medium"
                round="small"
                background="white"
                border={{ color: "light-4" }}
                direction="row"
                justify="between"
                align="center"
              >
                <Box direction="row" align="center" gap="medium">
                  <Box round="full" background="light-2" pad="small">
                    <Wifi color="dark-3" />
                  </Box>
                  <Box>
                    <Text weight="bold" size="large">
                      {door.name}
                    </Text>
                    <Text size="small" color="dark-6">
                      {door.type === "room"
                        ? t("gateways.room")
                        : t("gateways.area")}
                    </Text>
                  </Box>
                </Box>
                <Box align="end" gap="xsmall">
                  <Text weight="bold" color={signalColor}>
                    {signalLabel}
                  </Text>
                  <Text size="xsmall" color="dark-6">
                    {door.rssi || 0} dBm
                  </Text>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
